import { useLocation } from 'react-router';
import { useExternalCallbacks } from '../../hooks/useExternalCallbacks';
import {
  AuthenticationStorage,
  AuthLogger,
  buildOAuthCallbackUrl
} from '../../utils';
import { ExternalLoginCallback } from '../externalLoginCallback/externalLoginCallback';
import React, { useEffect, useState } from 'react';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { OIDC_CLIENT_STORAGE_KEY_PREFIX } from '../../constants';
import { useTranslation } from '@invision/core';

/**
 * 'redirecting' leaves the browser navigating to the external provider, so the
 * page stays in its progress state; 'completed' means the Ascendon session is
 * restored.
 */
type CallbackOutcome = 'redirecting' | 'completed';

/** Failure whose message is written for the user and safe to display. */
class OAuthCallbackError extends Error {}

export function OAuthCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const { handleExternalLoginSuccess } = useExternalCallbacks('oauth');
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    /**
     * Runs both legs of the authorization code flow on this route. Without
     * OAuth response parameters it starts the redirect to the external
     * provider; with them it exchanges the code for an access token and
     * restores the Ascendon session through Security/RestoreSession.
     */
    async function processOAuthCallback(): Promise<CallbackOutcome> {
      const oAuthSettings = AuthenticationStorage.getOAuthSettings();
      const queryParams = new URLSearchParams(location.search);
      const providerId = queryParams.get('providerId');
      const hasAuthParams = Boolean(
        (queryParams.get('code') || queryParams.get('error')) &&
          queryParams.get('state')
      );

      if (
        !providerId ||
        !oAuthSettings?.authority ||
        !oAuthSettings.clientId ||
        !oAuthSettings.systemId
      ) {
        // The combination identifies whether the callback URL is stale or the
        // settings were lost with the tab that started the sign in.
        AuthLogger.warn('OAuth callback is missing required inputs:', {
          hasProviderId: Boolean(providerId),
          hasAuthority: Boolean(oAuthSettings?.authority),
          hasClientId: Boolean(oAuthSettings?.clientId),
          hasSystemId: Boolean(oAuthSettings?.systemId),
          hasAuthParams
        });

        throw new OAuthCallbackError(
          t('Your sign in session has expired. Please sign in again.')
        );
      }

      const { authority, clientId, systemId, accessTokenPrefix } =
        oAuthSettings;

      // Holds the PKCE code verifier and request state for this sign in
      // attempt, and the provider user once the exchange completes.
      const oidcStore = new WebStorageStateStore({
        store: window.sessionStorage,
        prefix: OIDC_CLIENT_STORAGE_KEY_PREFIX
      });

      const userManager = new UserManager({
        authority,
        client_id: clientId,
        // Provider-specific discovery document. When absent, oidc-client-ts
        // derives it from the authority.
        metadataUrl: oAuthSettings.oidcDiscoveryUrl ?? undefined,
        stateStore: oidcStore,
        userStore: oidcStore,
        // The provider rejects the exchange unless this matches its registered
        // redirect URI, which is the callback URL the login page navigates to.
        redirect_uri: `${window.location.origin}${buildOAuthCallbackUrl(providerId)}`
      });

      if (!hasAuthParams) {
        await userManager.signinRedirect();

        return 'redirecting';
      }

      AuthenticationStorage.removeOAuthSettings();

      const user = await userManager.signinCallback();

      if (!user) {
        throw new OAuthCallbackError(
          t('Failed to complete authentication. Please try signing in again.')
        );
      }

      // The provider token is only ever exchanged for an Ascendon session, so
      // it is dropped here rather than left in storage for the tab's lifetime.
      // A later sign in obtains a fresh one, silently while the provider
      // session lasts.
      await userManager.removeUser();

      await handleExternalLoginSuccess(
        user.access_token,
        systemId,
        accessTokenPrefix
      );

      return 'completed';
    }

    processOAuthCallback()
      .then((outcome) => {
        if (!cancelled && 'completed' === outcome) {
          setIsProcessing(false);
        }
      })
      .catch((err) => {
        AuthLogger.error('OAuth callback processing failed:', err);
        AuthenticationStorage.removeOAuthSettings();

        if (cancelled) {
          return;
        }

        setError(
          err instanceof OAuthCallbackError
            ? err.message
            : t(
                'Failed to complete authentication. Please try signing in again.'
              )
        );
        setIsProcessing(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function handleReturnToLogin() {
    AuthenticationStorage.clearOAuthFlow();
  }

  return (
    <ExternalLoginCallback
      isProcessing={isProcessing}
      error={error}
      onReturnToLogin={handleReturnToLogin}
    />
  );
}
